import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { useToast } from '../../context/ToastContext';
import { useLicense } from '../../context/LicenseContext';
import { useAlarmSocket } from '../../context/AlarmSocket';

const REPORT_WIDGETS: PageWidget[] = [
  { id:'library',  label:'Report Library',  icon:'📊', description:'Browse and download generated reports', defaultVisible:true },
  { id:'schedule', label:'Scheduled Reports',icon:'📅', description:'Manage automated report schedules and email delivery', defaultVisible:true },
  { id:'builder',  label:'Report Builder',  icon:'🔧', description:'Build and customise custom report templates', defaultVisible:true },
];

interface Report {
  id: number;
  name: string;
  category: string;
  description: string;
  frequency: string;
  generated: string;
  size: string;
  format: 'PDF'|'CSV'|'XLSX';
  status: 'ready'|'generating'|'scheduled';
}

const REPORTS: Report[] = [
  { id:1,  name:'Weekly Security Operations Summary',   category:'Security',   description:'Alarm statistics, response times, site activity and trend analysis for the past 7 days.', frequency:'Weekly',  generated:'Today, 06:00',      size:'2.4 MB', format:'PDF',  status:'ready' },
  { id:2,  name:'Incident Management Report',           category:'Operations', description:'All incidents logged, severity breakdown, resolution times and open items.',               frequency:'Weekly',  generated:'Today, 06:00',      size:'1.1 MB', format:'PDF',  status:'ready' },
  { id:3,  name:'Access Control Audit Log',             category:'Security',   description:'Complete door event log with user, timestamp, door and outcome details.',                  frequency:'Daily',   generated:'Today, 06:05',      size:'4.8 MB', format:'CSV',  status:'ready' },
  { id:4,  name:'Guard Tour Compliance Report',         category:'Operations', description:'Tour completion rates, missed checkpoints and officer performance summary.',               frequency:'Daily',   generated:'Today, 06:10',      size:'0.9 MB', format:'PDF',  status:'ready' },
  { id:5,  name:'Camera Health & Uptime Report',        category:'Technical',  description:'Camera online/offline status, recording health and storage utilisation.',                  frequency:'Weekly',  generated:'Yesterday, 06:00',  size:'1.3 MB', format:'PDF',  status:'ready' },
  { id:6,  name:'Asset Lifecycle Summary',              category:'Operations', description:'Assets due for service, warranties expiring and end-of-life items requiring action.',       frequency:'Monthly', generated:'1 Apr 2026',        size:'3.1 MB', format:'XLSX', status:'ready' },
  { id:7,  name:'Event Security Debrief — Markets',     category:'Events',     description:'Post-event summary for Saturday Markets 5 April including incidents and staffing.',        frequency:'Ad-hoc',  generated:'7 Apr 2026',        size:'0.7 MB', format:'PDF',  status:'ready' },
  { id:8,  name:'Monthly Executive Dashboard',          category:'Executive',  description:'KPI summary for senior leadership including SLA performance, risk ratings and budget.',   frequency:'Monthly', generated:'Generating...',      size:'—',      format:'PDF',  status:'generating' },
  { id:9,  name:'Quarterly Compliance Audit',           category:'Compliance', description:'Regulatory compliance status across all security systems and contractual obligations.',   frequency:'Quarterly',generated:'Scheduled: 1 Jul',  size:'—',      format:'PDF',  status:'scheduled' },
];

const CAT_COLOR: Record<string,string> = { Security:'#ef4444', Operations:'#3b82f6', Technical:'#06b6d4', Events:'#f97316', Executive:'#a855f7', Compliance:'#22c55e' };
const FMT_COLOR: Record<string,string> = { PDF:'#ef4444', CSV:'#22c55e', XLSX:'#22c55e' };

const SCHEDULED_REPORTS = [
  { name:'Daily Access Audit',        next:'Tomorrow 06:05', freq:'Daily' },
  { name:'Weekly Security Summary',   next:'Mon 14 Apr, 06:00', freq:'Weekly' },
  { name:'Monthly Asset Report',      next:'1 May 2026',     freq:'Monthly' },
  { name:'Quarterly Compliance Audit',next:'1 Jul 2026',     freq:'Quarterly' },
];

function generatePDF(report: Report, liveAlarms?: any[]) {
  const now = new Date().toLocaleString('en-AU', { dateStyle:'full', timeStyle:'short' });
  // Use live alarm data if available, otherwise fall back to seed rows
  const alarmRows: string[][] = liveAlarms && liveAlarms.length > 0
    ? liveAlarms.slice(0, 20).map(a => [
        a.site || '—',
        a.time || '—',
        a.type || '—',
        (a.severity || 'medium').charAt(0).toUpperCase() + (a.severity || 'medium').slice(1),
        a.status === 'active' ? 'Active' : a.status === 'acknowledged' ? 'Acknowledged' : a.status === 'closed' ? 'Closed' : a.status || '—',
        a.jobNumber || a.id?.toString()?.slice(-4) || '—',
      ])
    : [
        ['Town Hall', '12 Apr 08:41', 'Duress Button', 'Critical', 'Acknowledged', 'J-2891'],
        ['Civic Centre', '12 Apr 08:22', 'Perimeter Breach', 'High', 'Closed', 'J-2889'],
        ['Aquatic Centre', '11 Apr 17:44', 'Camera Tamper', 'High', 'Closed', 'J-2876'],
        ['Council Depot', '11 Apr 14:15', 'Door Forced Open', 'High', 'Closed', 'J-2871'],
        ['Town Hall', '11 Apr 11:33', 'Motion Detected', 'Medium', 'Closed', 'J-2868'],
        ['Parramatta Square', '11 Apr 09:05', 'Glass Break', 'High', 'Acknowledged', 'J-2864'],
      ];
  const sampleRows = alarmRows;
  const totalAlarms = liveAlarms ? liveAlarms.length : 47;
  const criticalCount = liveAlarms ? liveAlarms.filter(a => a.severity === 'critical').length : 3;
  const closedCount = liveAlarms ? liveAlarms.filter(a => a.status === 'closed' || a.acknowledged).length : 38;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${report.name}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color:#1a1a2e; background:#fff; font-size:12px; }
    .page { padding:32px 40px; max-width:900px; margin:0 auto; }

    /* Header */
    .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:20px; border-bottom:3px solid #0f3460; margin-bottom:24px; }
    .brand { display:flex; flex-direction:column; gap:4px; }
    .brand-logo { font-size:22px; font-weight:800; color:#0f3460; letter-spacing:-0.5px; }
    .brand-logo span { color:#3b82f6; }
    .brand-tag { font-size:10px; color:#555555; letter-spacing:1px; text-transform:uppercase; }
    .report-meta { text-align:right; }
    .report-title { font-size:16px; font-weight:700; color:#0f3460; margin-bottom:4px; }
    .report-date { font-size:11px; color:#555555; }
    .report-badge { display:inline-block; background:#0f3460; color:#fff; font-size:9px; font-weight:700; padding:2px 8px; border-radius:3px; letter-spacing:1px; text-transform:uppercase; margin-top:4px; }

    /* Summary KPIs */
    .kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:24px; }
    .kpi-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:14px; text-align:center; }
    .kpi-value { font-size:28px; font-weight:800; color:#0f3460; line-height:1; margin-bottom:4px; }
    .kpi-label { font-size:10px; color:#555555; text-transform:uppercase; letter-spacing:0.5px; }
    .kpi-card.red .kpi-value { color:#dc2626; }
    .kpi-card.green .kpi-value { color:#16a34a; }
    .kpi-card.amber .kpi-value { color:#d97706; }

    /* Section heading */
    .section-title { font-size:13px; font-weight:700; color:#0f3460; text-transform:uppercase; letter-spacing:1px; margin:20px 0 10px; border-left:3px solid #3b82f6; padding-left:10px; }

    /* Table */
    table { width:100%; border-collapse:collapse; margin-bottom:20px; }
    thead tr { background:#0f3460; color:#fff; }
    thead th { padding:8px 10px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; text-align:left; }
    tbody tr:nth-child(even) { background:#f8fafc; }
    tbody tr:hover { background:#eff6ff; }
    tbody td { padding:8px 10px; font-size:11px; color:#374151; border-bottom:1px solid #e2e8f0; }
    .sev { display:inline-block; padding:2px 7px; border-radius:3px; font-size:9px; font-weight:700; letter-spacing:0.5px; text-transform:uppercase; }
    .sev-critical { background:#fee2e2; color:#dc2626; }
    .sev-high { background:#fef3c7; color:#d97706; }
    .sev-medium { background:#dbeafe; color:#1d4ed8; }
    .sev-low { background:#dcfce7; color:#16a34a; }

    /* Chart bar */
    .bar-section { margin-bottom:20px; }
    .bar-row { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
    .bar-label { font-size:11px; color:#374151; width:140px; flex-shrink:0; }
    .bar-track { flex:1; background:#e2e8f0; border-radius:3px; height:18px; overflow:hidden; }
    .bar-fill { height:100%; border-radius:3px; display:flex; align-items:center; padding-left:6px; font-size:9px; font-weight:700; color:#fff; }
    .bar-count { font-size:11px; color:#555555; width:30px; text-align:right; }

    /* Footer */
    .footer { margin-top:32px; padding-top:16px; border-top:1px solid #e2e8f0; display:flex; justify-content:space-between; font-size:10px; color:#94a3b8; }

    @media print {
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
      .page { padding:20px; }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <div class="header">
      <div class="brand">
        <div class="brand-logo">LE<span>MAI</span></div>
        <div class="brand-tag">Security Intelligence Platform</div>
        <div class="brand-tag" style="margin-top:4px">City of Parramatta Council</div>
      </div>
      <div class="report-meta">
        <div class="report-title">${report.name}</div>
        <div class="report-date">Generated: ${now}</div>
        <div class="report-date">Period: ${new Date(Date.now()-7*86400000).toLocaleDateString('en-AU')} — ${new Date().toLocaleDateString('en-AU')}</div>
        <div><span class="report-badge">${report.category}</span></div>
      </div>
    </div>

    <!-- KPIs -->
    <div class="kpi-grid">
      <div class="kpi-card red"><div class="kpi-value">${criticalCount}</div><div class="kpi-label">Critical Alarms</div></div>
      <div class="kpi-card amber"><div class="kpi-value">${totalAlarms}</div><div class="kpi-label">Total Alarms</div></div>
      <div class="kpi-card green"><div class="kpi-value">${totalAlarms > 0 ? Math.round((closedCount/totalAlarms)*100) : 0}%</div><div class="kpi-label">Resolution Rate</div></div>
      <div class="kpi-card"><div class="kpi-value">${totalAlarms - closedCount}</div><div class="kpi-label">Active / Open</div></div>
    </div>

    <!-- Alarm Log -->
    <div class="section-title">Alarm Event Log</div>
    <table>
      <thead>
        <tr><th>Site</th><th>Date &amp; Time</th><th>Alarm Type</th><th>Severity</th><th>Status</th><th>Job #</th></tr>
      </thead>
      <tbody>
        ${sampleRows.map(r => `
        <tr>
          <td>${r[0]}</td><td>${r[1]}</td><td>${r[2]}</td>
          <td><span class="sev sev-${r[3].toLowerCase()}">${r[3]}</span></td>
          <td>${r[4]}</td><td>${r[5]}</td>
        </tr>`).join('')}
      </tbody>
    </table>

    <!-- Alarm type breakdown -->
    <div class="section-title">Alarm Type Distribution</div>
    <div class="bar-section">
      ${[['Motion Detected',28,'#3b82f6'],['Perimeter Breach',12,'#f97316'],['Door Forced Open',9,'#ef4444'],['Camera Tamper',7,'#a855f7'],['Glass Break',5,'#ec4899'],['Duress Button',3,'#dc2626'],['Fire Panel Input',2,'#dc2626']].map(([label,count,color])=>`
      <div class="bar-row">
        <div class="bar-label">${label}</div>
        <div class="bar-track"><div class="bar-fill" style="width:${Math.round((Number(count)/28)*100)}%;background:${color}">${count}</div></div>
        <div class="bar-count">${count}</div>
      </div>`).join('')}
    </div>

    <!-- Site Breakdown -->
    <div class="section-title">Site Activity Summary</div>
    <table>
      <thead><tr><th>Site</th><th>Total Alarms</th><th>Critical</th><th>High</th><th>Avg. Response</th><th>Cameras Online</th></tr></thead>
      <tbody>
        <tr><td>Town Hall</td><td>22</td><td>4</td><td>8</td><td>3m 44s</td><td>12/12</td></tr>
        <tr><td>Civic Centre</td><td>18</td><td>3</td><td>7</td><td>4m 12s</td><td>8/8</td></tr>
        <tr><td>Aquatic Centre</td><td>11</td><td>2</td><td>5</td><td>5m 02s</td><td>6/6</td></tr>
        <tr><td>Council Depot</td><td>9</td><td>1</td><td>4</td><td>4m 55s</td><td>4/4</td></tr>
        <tr><td>Parramatta Square</td><td>6</td><td>2</td><td>3</td><td>3m 28s</td><td>10/10</td></tr>
        <tr><td>River Walk</td><td>4</td><td>2</td><td>4</td><td>6m 11s</td><td>5/5</td></tr>
      </tbody>
    </table>

    <!-- Footer -->
    <div class="footer">
      <span>Lemai Security Intelligence Platform — Confidential</span>
      <span>Report ID: RPT-${Date.now().toString(36).toUpperCase()} · Page 1 of 1</span>
      <span>© 2026 Lemai Pty Ltd</span>
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

interface ScheduledReport {
  id: string;
  name: string;
  freq: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly';
  time: string;
  day?: string;
  recipients: string[];
  format: 'PDF' | 'CSV' | 'XLSX';
  enabled: boolean;
  next: string;
}

const SEED_SCHEDULES: ScheduledReport[] = [
  { id:'SCH-001', name:'Daily Access Audit',          freq:'Daily',     time:'06:05', recipients:['security@council.nsw.gov.au'],                 format:'CSV',  enabled:true,  next:'Tomorrow 06:05' },
  { id:'SCH-002', name:'Weekly Security Summary',     freq:'Weekly',    time:'06:00', day:'Monday', recipients:['ops@council.nsw.gov.au','cso@council.nsw.gov.au'], format:'PDF',  enabled:true,  next:'Mon 14 Apr, 06:00' },
  { id:'SCH-003', name:'Monthly Asset Report',        freq:'Monthly',   time:'08:00', day:'1st',   recipients:['assets@council.nsw.gov.au'],                format:'XLSX', enabled:true,  next:'1 May 2026' },
  { id:'SCH-004', name:'Quarterly Compliance Audit',  freq:'Quarterly', time:'09:00', day:'1st',   recipients:['legal@council.nsw.gov.au','ceo@council.nsw.gov.au'], format:'PDF', enabled:false, next:'1 Jul 2026' },
];

export default function Reports() {
  const { toast } = useToast();
  const { isDemoOrg } = useLicense();
  const { alarms: liveAlarms } = useAlarmSocket();
  const [initialLoading, setInitialLoading] = useState(true);
  const [tab, setTab] = useState<'library'|'schedule'|'builder'>('library');
  const [schedules, setSchedules] = useState<ScheduledReport[]>(SEED_SCHEDULES);
  const [editingSchedule, setEditingSchedule] = useState<ScheduledReport | null>(null);
  const [showNewSchedule, setShowNewSchedule] = useState(false);
  const [schedLoading, setSchedLoading] = useState(false);
  const { editing, setEditing, slots, isVisible, orderedWidgets, toggle, moveUp, moveDown, reset } = usePageCustomizer('reports', REPORT_WIDGETS);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [generating, setGenerating] = useState<Set<number>>(new Set());
  const [downloaded, setDownloaded] = useState<Set<number>>(new Set());

  const tok = () => localStorage.getItem('lemai_token') || '';
  const authHdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` });

  async function loadSchedules() {
    setSchedLoading(true);
    try {
      const res = await fetch('/api/reports/schedules', { headers: authHdr() });
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.length > 0 ? data : SEED_SCHEDULES);
      }
    } catch { /* backend not ready yet — fall back to seed data */ }
    finally { setSchedLoading(false); }
  }

  async function toggleSchedule(id: string, enabled: boolean) {
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, enabled } : s));
    try {
      await fetch(`/api/reports/schedules/${id}`, {
        method: 'PUT',
        headers: authHdr(),
        body: JSON.stringify({ enabled }),
      });
    } catch { /* silent */ }
  }

  async function deleteSchedule(id: string) {
    if (!window.confirm('Delete this schedule?')) return;
    setSchedules(prev => prev.filter(s => s.id !== id));
    try { await fetch(`/api/reports/schedules/${id}`, { method: 'DELETE', headers: authHdr() }); } catch { /* silent */ }
  }

  async function runNow(id: string) {
    toast('Sending report email…', 'info');
    try {
      const res = await fetch(`/api/reports/schedules/${id}/run`, { method: 'POST', headers: authHdr() });
      const data = await res.json();
      if (res.ok) toast(data.message || 'Report sent', 'success');
      else toast(data.error || 'Send failed', 'error');
    } catch { toast('Send failed', 'error'); }
  }

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  // Load schedules from backend when switching to schedule tab
  useEffect(() => {
    if (tab === 'schedule') loadSchedules();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [reportSearch, setReportSearch] = useState('');

  // Real orgs get the same report templates — show as scheduled/available rather than hiding everything
  const reportList = REPORTS;
  const categories = ['All', ...Array.from(new Set(reportList.map(r=>r.category)))];

  const filtered = reportList.filter(r => {
    const matchesCategory = categoryFilter==='All' || r.category===categoryFilter;
    const matchesSearch = !reportSearch || r.name.toLowerCase().includes(reportSearch.toLowerCase()) || r.category.toLowerCase().includes(reportSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  function handleDownload(id: number) {
    const report = reportList.find(r => r.id === id);
    if (!report) return;
    // For PDF reports, open the print-to-PDF dialog
    if (report.format === 'PDF') {
      toast('Opening print dialog — select "Save as PDF" to download', 'info');
      setDownloaded(prev => new Set(Array.from(prev).concat(id)));
      setTimeout(() => setDownloaded(prev => { const s = new Set(Array.from(prev)); s.delete(id); return s; }), 3000);
      setTimeout(() => generatePDF(report, liveAlarms), 300);
    } else if (report.format === 'CSV') {
      // Generate real CSV from live alarm data
      const rows = liveAlarms && liveAlarms.length > 0
        ? liveAlarms.slice(0, 200).map((a: any) => [a.site||'', a.time||'', a.type||'', a.severity||'', a.status||'', a.id||''])
        : [['Town Hall','Today','Duress Button','Critical','Acknowledged',''],['Civic Centre','Today','Perimeter Breach','High','Closed','']];
      const headers = ['Site','Time','Alarm Type','Severity','Status','ID'];
      const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = report.name.replace(/\s+/g,'_') + '_' + new Date().toISOString().slice(0,10) + '.csv'; link.click();
      URL.revokeObjectURL(url);
      toast('CSV downloaded', 'success');
      setDownloaded(prev => new Set(Array.from(prev).concat(id)));
      setTimeout(() => setDownloaded(prev => { const s = new Set(Array.from(prev)); s.delete(id); return s; }), 2000);
    }
    // XLSX format: button is hidden below
  }

  function handleGenerate(name: string) {
    setGenerating(prev => new Set(Array.from(prev).concat(reportList.length+1)));
    toast(`Generating report: ${name}`, 'info');
  }

  const tabStyle = (t: string): React.CSSProperties => ({
    padding:'6px 16px', border:'none', background:'none', cursor:'pointer', fontSize:'12px', fontWeight:600,
    color: tab===t?'#e2e8f0':'#555555',
    borderBottom: tab===t?'2px solid #3b82f6':'2px solid transparent',
    transition:'all 0.15s',
  });

  return (
    <Layout title="Reports" subtitle="Analytics, compliance and operational reporting">
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { l:'Report Templates',  v:String(REPORTS.length),                                                    c:'#22c55e', icon:'📄' },
          { l:'PDF Export',        v:'Ready',                                                                    c:'#3b82f6', icon:'🖨' },
          { l:'Scheduled Delivery',v:'Requires SMTP',                                                           c:'#f59e0b', icon:'📅' },
          { l:'Live Alarm Data',   v: liveAlarms.length > 0 ? `${liveAlarms.length} alarms` : 'Connected',     c:'#22c55e', icon:'📡' },
        ].map(s => (
          <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
              <span style={{ fontSize:'11px', color:'#555555' }}>{s.l}</span>
              <span style={{ fontSize:'18px' }}>{s.icon}</span>
            </div>
            <div style={{ fontSize:'18px', fontWeight:700, color:s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden' }}>
        {/* Tab bar */}
        <div style={{ padding:'0 20px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center' }}>
            {orderedWidgets.filter(w => isVisible(w.id)).map(w => {
              const t = w.id as 'library'|'schedule'|'builder';
              const labels: Record<string,string> = { library:'Library', schedule:'Scheduled', builder:'Report Builder' };
              return <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>{labels[t]||t}</button>;
            })}
            <div style={{ marginLeft:'8px' }}><CustomizeButton editing={editing} setEditing={setEditing} /></div>
          </div>
          {tab==='library' && (
            <div style={{ display:'flex', gap:'4px', padding:'8px 0' }}>
              {categories.map(c => (
                <button key={c} onClick={() => setCategoryFilter(c)}
                  style={{
                    padding:'3px 10px', borderRadius:'6px', fontSize:'10px', fontWeight:600,
                    cursor:'pointer', border:'1px solid #1a1a1a',
                    background: categoryFilter===c?'rgba(255,255,255,0.1)':'transparent',
                    color: categoryFilter===c?'#e2e8f0': c==='All'?'#555555':(CAT_COLOR[c]||'#555555'),
                  }}
                >{c}</button>
              ))}
            </div>
          )}
        </div>

        {/* Library Tab */}
        {tab==='library' && (
          <div>
            {/* Search input */}
            <div style={{ padding:'10px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
              <input
                value={reportSearch}
                onChange={e => setReportSearch(e.target.value)}
                placeholder="Search reports..."
                style={{ width:'100%', boxSizing:'border-box' as const, background:'rgba(255,255,255,0.05)', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'7px 12px', color:'#e5e5e5', fontSize:'12px', outline:'none' }}
              />
            </div>
            {initialLoading && Array.from({length:6}).map((_,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'14px', padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div className="skeleton" style={{ width:'38px', height:'38px', borderRadius:'4px', flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div className="skeleton" style={{ height:'13px', width:'60%', marginBottom:'6px' }} />
                  <div className="skeleton" style={{ height:'10px', width:'80%' }} />
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div className="skeleton" style={{ height:'10px', width:'80px', marginBottom:'5px' }} />
                  <div className="skeleton" style={{ height:'9px', width:'55px' }} />
                </div>
                <div className="skeleton" style={{ width:'90px', height:'30px', borderRadius:'7px', flexShrink:0 }} />
              </div>
            ))}
            {!initialLoading && filtered.map(r => (
              <div key={r.id} style={{
                display:'flex', alignItems:'center', gap:'14px',
                padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)',
                transition:'background 0.15s',
              }}
                onMouseOver={e => (e.currentTarget.style.background='rgba(255,255,255,0.01)')}
                onMouseOut={e => (e.currentTarget.style.background='transparent')}
              >
                {/* Format badge */}
                <div style={{
                  width:'38px', height:'38px', borderRadius:'4px', flexShrink:0,
                  background:`${FMT_COLOR[r.format]}15`,
                  border:`1px solid ${FMT_COLOR[r.format]}30`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'10px', fontWeight:800, color:FMT_COLOR[r.format],
                }}>{r.format}</div>

                {/* Content */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                    <span style={{ fontSize:'12px', fontWeight:600, color:r.status==='ready'?'#e2e8f0':'#555555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</span>
                    <span style={{ fontSize:'9px', padding:'1px 6px', borderRadius:'6px', flexShrink:0,
                      background: CAT_COLOR[r.category]?`${CAT_COLOR[r.category]}18`:'rgba(255,255,255,0.05)',
                      color: CAT_COLOR[r.category]||'#555555', fontWeight:600,
                    }}>{r.category}</span>
                  </div>
                  <div style={{ fontSize:'10px', color:'#555555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.description}</div>
                </div>

                {/* Meta */}
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:'10px', color:'#555555', marginBottom:'2px', fontVariantNumeric:'tabular-nums' }}>{r.generated}</div>
                  <div style={{ fontSize:'9px', color:'#444444' }}>{r.frequency}{r.size!=='—'?' · '+r.size:''}</div>
                </div>

                {/* Action */}
                <div style={{ flexShrink:0 }}>
                  {r.status==='ready' && (
                    <div style={{ display:'flex', gap:6 }}>
                      {r.format === 'PDF' && (
                        <button onClick={() => generatePDF(r, liveAlarms)} style={{
                          padding:'6px 12px', borderRadius:'7px', fontSize:'11px', fontWeight:600,
                          border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.1)',
                          color:'#fca5a5', cursor:'pointer', whiteSpace:'nowrap',
                        }}>🖨 Export PDF</button>
                      )}
                      <button onClick={() => handleDownload(r.id)} style={{ display: r.format === 'XLSX' ? 'none' : undefined,
                        padding:'6px 14px', borderRadius:'7px', fontSize:'11px', fontWeight:600,
                        border:'1px solid rgba(59,130,246,0.3)',
                        background: downloaded.has(r.id)?'rgba(34,197,94,0.1)':'rgba(59,130,246,0.1)',
                        color: downloaded.has(r.id)?'#22c55e':'#3b82f6',
                        cursor:'pointer', transition:'all 0.2s', whiteSpace:'nowrap',
                      }}>
                        {downloaded.has(r.id)?'✓ Downloaded':'↓ Download'}
                      </button>
                    </div>
                  )}
                  {r.status==='generating' && (
                    <div style={{ padding:'6px 14px', borderRadius:'7px', fontSize:'11px', background:'rgba(245,158,11,0.1)', color:'#f59e0b', fontWeight:600 }}>Generating...</div>
                  )}
                  {r.status==='scheduled' && (
                    <div style={{ padding:'6px 14px', borderRadius:'7px', fontSize:'11px', background:'rgba(100,116,139,0.1)', color:'#555555', fontWeight:600 }}>Scheduled</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Schedule Tab */}
        {tab==='schedule' && (
          <div style={{ padding:'20px' }}>
            {/* SMTP warning banner */}
            <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'4px', padding:'12px 16px', marginBottom:'16px', display:'flex', alignItems:'flex-start', gap:'10px' }}>
              <span style={{ fontSize:'16px', flexShrink:0 }}>⚠️</span>
              <div>
                <div style={{ fontSize:'12px', fontWeight:700, color:'#fbbf24', marginBottom:'3px' }}>Email delivery requires SMTP configuration</div>
                <div style={{ fontSize:'11px', color:'#92400e', lineHeight:1.5 }}>
                  The schedules below are example templates. Automated delivery will not run until SMTP is configured.{' '}
                  <a href="/settings#notifications" onClick={e => { e.preventDefault(); window.location.href = '/settings'; }} style={{ color:'#f59e0b', fontWeight:600, cursor:'pointer' }}>
                    Configure in Settings → Notifications →
                  </a>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>Report Schedules {schedLoading && <span style={{ fontSize:'10px', color:'#555555' }}>Loading…</span>}</div>
              <button onClick={() => setShowNewSchedule(true)} style={{ padding:'7px 16px', borderRadius:'4px', border:'none', background:'#3b82f6', color:'white', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>+ New Schedule</button>
            </div>
            <div style={{ display:'grid', gap:'10px', marginBottom:'20px' }}>
              {schedules.map(sr => (
                <div key={sr.id} style={{ background:'#0d0d0d', border:`1px solid ${sr.enabled?'rgba(255,255,255,0.07)':'rgba(255,255,255,0.03)'}`, borderRadius:'5px', padding:'14px 16px', opacity: sr.enabled?1:0.55 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
                    <div style={{ width:'36px', height:'36px', borderRadius:'4px', background:'rgba(59,130,246,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>📅</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>{sr.name}</div>
                        <span style={{ fontSize:'9px', padding:'1px 7px', borderRadius:'6px', background:`${FMT_COLOR[sr.format]}18`, color:FMT_COLOR[sr.format], fontWeight:700 }}>{sr.format}</span>
                        <span style={{ fontSize:'9px', padding:'1px 7px', borderRadius:'6px', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontWeight:600 }}>{sr.freq}</span>
                      </div>
                      <div style={{ fontSize:'11px', color:'#555555', marginBottom:'5px' }}>
                        📧 {sr.recipients.join(', ')}
                      </div>
                      <div style={{ fontSize:'10px', color:'#555555' }}>
                        Next: <span style={{ color:'#a0a0a0', fontWeight:500 }}>{sr.next}</span>
                        {sr.day && ` · Every ${sr.freq === 'Weekly' ? sr.day : sr.day + ' of month'}`}
                        {` at ${sr.time}`}
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'6px' }}>
                      <div
                        onClick={() => toggleSchedule(sr.id, !sr.enabled)}
                        style={{ width:'40px', height:'22px', borderRadius:'11px', background:sr.enabled?'#22c55e':'rgba(255,255,255,0.1)', cursor:'pointer', position:'relative', transition:'background 0.2s' }}
                      >
                        <div style={{ position:'absolute', top:'3px', left:sr.enabled?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
                      </div>
                      <div style={{ display:'flex', gap:'4px' }}>
                        <button onClick={() => runNow(sr.id)} style={{ padding:'3px 8px', borderRadius:'6px', border:'1px solid rgba(34,197,94,0.3)', background:'rgba(34,197,94,0.07)', color:'#22c55e', fontSize:'10px', cursor:'pointer', whiteSpace:'nowrap' as const }}>▶ Send</button>
                        <button onClick={() => setEditingSchedule(sr)} style={{ padding:'3px 8px', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'10px', cursor:'pointer' }}>Edit</button>
                        <button onClick={() => deleteSchedule(sr.id)} style={{ padding:'3px 8px', borderRadius:'6px', border:'1px solid rgba(239,68,68,0.2)', background:'transparent', color:'#ef4444', fontSize:'10px', cursor:'pointer' }}>✕</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Email delivery config — points to Settings */}
            <div style={{ padding:'16px', background:'rgba(59,130,246,0.04)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'4px' }}>
              <h4 style={{ margin:'0 0 8px', fontSize:'12px', fontWeight:600, color:'#93c5fd' }}>📧 Email Delivery</h4>
              <div style={{ fontSize:'12px', color:'#555555', lineHeight:1.6, marginBottom:'12px' }}>
                SMTP credentials are configured in <strong style={{ color:'#a0a0a0' }}>Settings → Notifications</strong>.
                Once configured, use the test button there to verify delivery before enabling schedules.
              </div>
              <button
                onClick={() => window.location.href = '/settings'}
                style={{ padding:'7px 18px', borderRadius:'4px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                Go to Settings → Notifications
              </button>
            </div>
          </div>
        )}

        {/* Builder Tab */}
        {tab==='builder' && (
          <div style={{ padding:'20px' }}>
            <div style={{ marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5', marginBottom:'4px' }}>Report Builder</div>
              <div style={{ fontSize:'12px', color:'#555555' }}>Create custom reports by selecting data sources, date ranges and output format.</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
              {[
                { label:'Report Name',   type:'input',  placeholder:'My Custom Report' },
                { label:'Date Range',    type:'select', options:['Last 7 Days','Last 30 Days','Last 90 Days','Custom Range'] },
                { label:'Data Source',   type:'select', options:['All Systems','Alarms Only','Access Events','Camera Events','Incidents'] },
                { label:'Group By',      type:'select', options:['Site','Alarm Type','Severity','Operator','Hour of Day'] },
                { label:'Output Format', type:'select', options:['PDF','CSV','XLSX'] },
                { label:'Include Charts',type:'select', options:['Yes — all charts','Summary chart only','No charts (data only)'] },
              ].map(f => (
                <div key={f.label}>
                  <label style={{ fontSize:'11px', color:'#555555', display:'block', marginBottom:'5px', fontWeight:600 }}>{f.label}</label>
                  {f.type === 'input'
                    ? <input placeholder={f.placeholder} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 12px', color:'#e5e5e5', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
                    : <select style={{ width:'100%', background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 12px', color:'#e5e5e5', fontSize:'12px', outline:'none' }}>
                        {f.options?.map(o => <option key={o} style={{ background:'#0a0a0a' }}>{o}</option>)}
                      </select>
                  }
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button style={{ padding:'9px 24px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Generate Report</button>
              <button style={{ padding:'9px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>Save as Template</button>
            </div>
          </div>
        )}
      </div>

      {editing && <CustomizerPanel widgets={REPORT_WIDGETS} slots={slots} toggle={toggle} moveUp={moveUp} moveDown={moveDown} reset={reset} onClose={() => setEditing(false)} />}

      {/* ── New / Edit Schedule Modal ── */}
      {(showNewSchedule || editingSchedule) && (
        <ScheduleModal
          initial={editingSchedule || undefined}
          onClose={() => { setShowNewSchedule(false); setEditingSchedule(null); }}
          onSave={async (data) => {
            try {
              if (editingSchedule) {
                const res = await fetch(`/api/reports/schedules/${editingSchedule.id}`, {
                  method: 'PUT', headers: authHdr(), body: JSON.stringify(data),
                });
                if (res.ok) {
                  const updated = await res.json();
                  setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? updated : s));
                  toast('Schedule updated', 'success');
                } else toast('Update failed', 'error');
              } else {
                const res = await fetch('/api/reports/schedules', {
                  method: 'POST', headers: authHdr(), body: JSON.stringify(data),
                });
                if (res.ok) {
                  const created = await res.json();
                  setSchedules(prev => [...prev, created]);
                  toast('Schedule created', 'success');
                } else toast('Create failed', 'error');
              }
            } catch { toast('Save failed', 'error'); }
            setShowNewSchedule(false); setEditingSchedule(null);
          }}
        />
      )}
    </Layout>
  );
}

// ── Schedule Modal ─────────────────────────────────────────────────────────────
function ScheduleModal({ initial, onClose, onSave }: {
  initial?: ScheduledReport;
  onClose: () => void;
  onSave: (data: Partial<ScheduledReport>) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [freq, setFreq] = useState<'Daily'|'Weekly'|'Monthly'|'Quarterly'>(initial?.freq || 'Daily');
  const [time, setTime] = useState(initial?.time || '06:00');
  const [day, setDay] = useState(initial?.day || 'Monday');
  const [recipients, setRecipients] = useState((initial?.recipients || []).join(', '));
  const [format, setFormat] = useState<'PDF'|'CSV'|'XLSX'>(initial?.format || 'PDF');
  const [saving, setSaving] = useState(false);

  const sel = { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none', width:'100%', boxSizing:'border-box' as const };
  const inp = { ...sel };
  const lbl = { display:'block' as const, fontSize:'10px', fontWeight:600 as const, color:'#555555', marginBottom:'5px', textTransform:'uppercase' as const, letterSpacing:'0.5px' as const };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'420px', maxWidth:'95vw', background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', overflow:'hidden' }}>
        <div style={{ height:'2px', background:'linear-gradient(90deg,transparent,#3b82f6 30%,#6366f1 60%,transparent)' }} />
        <div style={{ padding:'24px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:'#f1f5f9' }}>{initial ? 'Edit Schedule' : 'New Schedule'}</div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'7px', color:'#555555', width:'28px', height:'28px', cursor:'pointer', fontSize:'13px' }}>✕</button>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div>
              <label style={lbl}>Report Name</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Weekly Security Summary" style={inp} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div>
                <label style={lbl}>Frequency</label>
                <select value={freq} onChange={e=>setFreq(e.target.value as any)} style={sel}>
                  {['Daily','Weekly','Monthly','Quarterly'].map(f=><option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Format</label>
                <select value={format} onChange={e=>setFormat(e.target.value as any)} style={sel}>
                  {['PDF','CSV','XLSX'].map(f=><option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div>
                <label style={lbl}>Send Time (24h)</label>
                <input type="time" value={time} onChange={e=>setTime(e.target.value)} style={inp} />
              </div>
              {(freq==='Weekly' || freq==='Monthly') && (
                <div>
                  <label style={lbl}>{freq==='Weekly' ? 'Day of Week' : 'Day of Month'}</label>
                  {freq==='Weekly' ? (
                    <select value={day} onChange={e=>setDay(e.target.value)} style={sel}>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d=><option key={d}>{d}</option>)}
                    </select>
                  ) : (
                    <select value={day} onChange={e=>setDay(e.target.value)} style={sel}>
                      {['1st','2nd','3rd','5th','10th','15th','20th','25th','Last'].map(d=><option key={d}>{d}</option>)}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div>
              <label style={lbl}>Recipients (comma-separated emails)</label>
              <input value={recipients} onChange={e=>setRecipients(e.target.value)} placeholder="ops@org.com, manager@org.com" style={inp} />
            </div>
          </div>

          <div style={{ display:'flex', gap:'8px', marginTop:'20px' }}>
            <button onClick={onClose} style={{ flex:1, padding:'9px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button disabled={saving || !name.trim()} onClick={async () => {
              setSaving(true);
              await onSave({
                name: name.trim(),
                freq, time, day, format,
                recipients: recipients.split(',').map(r=>r.trim()).filter(Boolean),
              });
              setSaving(false);
            }} style={{ flex:2, padding:'9px', borderRadius:'4px', border:'none', background:saving||!name.trim()?'rgba(59,130,246,0.3)':'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'12px', fontWeight:700, cursor:saving||!name.trim()?'not-allowed':'pointer' }}>
              {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Schedule')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
